import { ProgressBar } from "./ProgressBar";

export interface PasswordStrengthCriterion {
  key: string;
  label: string;
  met: boolean;
}

export interface PasswordStrengthMeterProps {
  password: string;
  /** Platform-enforced floor — Saleslox requires 12. */
  minLength?: number;
}

function evaluate(password: string, minLength: number): { criteria: PasswordStrengthCriterion[]; score: number } {
  const criteria: PasswordStrengthCriterion[] = [
    { key: "length", label: `${minLength} chars`, met: password.length >= minLength },
    { key: "upper", label: "A-Z", met: /[A-Z]/.test(password) },
    { key: "lower", label: "a-z", met: /[a-z]/.test(password) },
    { key: "digit", label: "123", met: /[0-9]/.test(password) },
    { key: "symbol", label: "@#$", met: /[^A-Za-z0-9]/.test(password) },
  ];
  const score = criteria.filter((c) => c.met).length;
  return { criteria, score };
}

const LEVEL = [
  { label: "Too short", tone: "danger" as const, pct: 15 },
  { label: "Weak", tone: "danger" as const, pct: 35 },
  { label: "Fair", tone: "warning" as const, pct: 55 },
  { label: "Good", tone: "warning" as const, pct: 75 },
  { label: "Strong", tone: "success" as const, pct: 90 },
  { label: "Excellent", tone: "success" as const, pct: 100 },
];

/** Live strength feedback while the user types. Purely advisory — the platform's
 * only enforced rule is the minimum length; the composition checkmarks are
 * guidance, not requirements the API will reject on. */
export function PasswordStrengthMeter({ password, minLength = 12 }: PasswordStrengthMeterProps) {
  if (!password) return null;
  const { criteria, score } = evaluate(password, minLength);
  const meetsMin = password.length >= minLength;
  const levelIndex = meetsMin ? Math.min(score, LEVEL.length - 1) : 0;
  const level = LEVEL[levelIndex];

  return (
    <div style={{ display: "grid", gap: "var(--space-2)" }} aria-live="polite">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "var(--font-size-xs)",
          color: "var(--color-text-muted)",
        }}
      >
        <span>Password strength</span>
        <span style={{ color: `var(--color-${level.tone})`, fontWeight: "var(--font-weight-medium)" }}>
          {level.label}
        </span>
      </div>
      <ProgressBar value={level.pct} tone={level.tone} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
        {criteria.map((criterion) => (
          <span
            key={criterion.key}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "var(--space-1)",
              fontSize: "var(--font-size-xs)",
              color: criterion.met ? "var(--color-success)" : "var(--color-text-muted)",
            }}
          >
            <span aria-hidden="true">{criterion.met ? "✓" : "○"}</span>
            {criterion.label}
          </span>
        ))}
      </div>
    </div>
  );
}
